import { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import { useOrganizationDetailsStore } from '@libs/store/settings';
import { OrganizationDetailsForm } from '@libs/models/settings';
import { useNotification } from '@libs/snackbar';
import { parseError } from '@libs/api/errors';
import { Form } from '@molecules/form/form';

import { SettingsForm } from '@templates';
import { SettingsPagePane } from './components';

export function OrganizationDetailsSettings() {
  const { showError, showSuccessIntl } = useNotification();
  const { load, save, loading: orgSettingsLoading, data } = useOrganizationDetailsStore();

  const initialData = useMemo(() => {
    if (data) {
      return {
        name: data.name,
        address1: data.addressDetails.firstAddress,
        npi: data.npi,
        agency_phone_number: data.phoneNumber,
        employer_identification_number: data.ein,
        website: data.websiteURL,
        postal_code: data.addressDetails.zipCode,
        numberOfEmployees:
          data.numberOfEmployeesMaxValue === null
            ? `${data.numberOfEmployeesMinValue}+`
            : `${data.numberOfEmployeesMinValue}-${data.numberOfEmployeesMaxValue}`,
        organizationType: data.facilityTypes ?? [],
        registration_date: dayjs(data.createdOn),
        cityAndState: [data.addressDetails.city, data.addressDetails.state].join(', '),
        primary_contact_full_name: data.mainContact.name,
        job_title: data.mainContact.title,
        primary_phone_number: data.mainContact.phoneNumber,
        secondary_number: data.mainContact.secondaryPhoneNumber,
        email: data.mainContact.email,
      };
    }
    return null;
  }, [data]);

  const methods = useForm<OrganizationDetailsForm>({
    defaultValues: {
      numberOfEmployees: '1-100',
      line_of_business: [],
      job_qualifications: [],
      organizationType: [],
      working_week_start: 1,
      address1: null,
    },
  });
  const {
    reset,
    formState: { errors },
  } = methods;
  const loading = orgSettingsLoading;
  const errorsNumber = Object.keys(errors).length;
  const onSubmit = async (data: OrganizationDetailsForm) => {
    try {
      const employees = data.numberOfEmployees.split('-');
      const cityAndState = data.cityAndState ?? '';
      const [city, state] = cityAndState.split(', ');
      await save({
        id: '',
        ein: data.employer_identification_number,
        npi: data.npi,
        createdOn: dayjs(data.registration_date).toServerDateTimeFormat(),
        websiteURL: data.website || '',
        name: data.name,
        phoneNumber: data.agency_phone_number,
        numberOfEmployeesMinValue: parseInt(employees[0], 10),
        numberOfEmployeesMaxValue: parseInt(employees[1], 10),
        addressDetails: {
          city,
          state,
          firstAddress: data.address1 || '',
          secondAddress: '',
          zipCode: data.postal_code,
        },
        mainContact: {
          email: data.email,
          name: data.primary_contact_full_name,
          phoneNumber: data.primary_phone_number,
          secondaryPhoneNumber: data.secondary_number || '',
          title: data.job_title,
        },
        facilityTypes: data.organizationType,
      });
      showSuccessIntl('settings.organization.savedSuccessfully');
    } catch (err) {
      const error = parseError(err);
      showError(error.message);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (initialData) {
      // Set data from the server to the form
      reset(initialData);
    }
  }, [initialData, reset]);

  return (
    <SettingsPagePane>
      <Form formUtils={methods} onSubmit={onSubmit}>
        <SettingsForm
          title={<FormattedMessage id="settings.organization.title" />}
          subtitle={<FormattedMessage id="settings.organization.subtitle" />}
          message={
            errorsNumber > 0 && <FormattedMessage id="forms.requiredFieldsMissing" values={{ number: errorsNumber }} />
          }
          loading={loading}
          messageType="error"
          onCancel={() => initialData && reset(initialData)}
          parts={[
            // <SettingsOrganizationMainDetails key="mainDetails" loading={loading} />,
            // <SettingsOrganizationAddress key="address" loading={loading} />,
            // <SettingsOrganizationContact key="contact" loading={loading} />,
            // // <SettingsOrganizationOperational key="operational" loading={loading} />,
          ]}
        />
      </Form>
    </SettingsPagePane>
  );
}
